import { Autocomplete, Box } from "@mui/material";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { MecordColors } from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";
import { showConfirm } from "helpers/swalHelper";
import React, { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { fetchSupplierById } from "slices/Masters/supplier/thunk";

interface SupplierOption {
  supplier_id: string;
  supplier_name: string;
  email: string;
  phone_number: string;
  contact_person_name: string | null;
  contact_person_email: string | null;
  contact_person_phone_no: string | null;
  basic_details?: Array<{
    branch_contact_name: string;
    contact_person_email: string;
    contact_person_phone_no: string;
    branch_email: string;
    phone_no: string;
  }>;
}

interface SupplierFormValues {
  supplier: SupplierOption | null;
  contact_details: any;
}

const SupplierDetails: React.FC<{
  formik: any;
  pr_data: any;
  pr_supplier_data: any;
  branchOptions: any;
  supplierOptions: any[];
  set_pr_supplier_data?: any;
  supplierLoading?: boolean;
  editMode?: boolean;
}> = ({
  formik,
  pr_data,
  pr_supplier_data,
  branchOptions,
  supplierOptions,
  set_pr_supplier_data,
  supplierLoading = false,
  editMode = false,
}) => {
  const dispatch = useDispatch<any>();
  const [contactOptions, setContactOptions] = useState<any>([]);
  const [previousSupplierId, setPreviousSupplierId] = useState(
    formik.values.supplier_id,
  );
  async function loadSupplierById(supplier_id: string) {
    // setLoadingSupplierData(true);
    try {
      const res = await dispatch(fetchSupplierById(supplier_id)).unwrap();
      // setSupplierData(res || []);
      setContactOptions(res?.contacts || []);
    } catch (error) {
      toast.error("Failed to load models");
    } finally {
      // setLoadingSupplierData(false);
    }
  }

  useEffect(() => {
    if (formik.values.supplier_id) {
      loadSupplierById(formik.values.supplier_id);
    }
  }, [formik.values.supplier_id]);

  useEffect(() => {
    setPreviousSupplierId(formik.values.supplier_id);
  }, []);

  useEffect(() => {
    if (
      editMode &&
      contactOptions.length > 0 &&
      pr_supplier_data?.contact_person_name &&
      (!formik.values.contact_person ||
        !formik.values.contact_person.supplier_contact_id)
    ) {
      const name = String(pr_supplier_data.contact_person_name)
        .trim()
        .toLowerCase();
      const email = pr_supplier_data.contact_person_email
        ? String(pr_supplier_data.contact_person_email).trim().toLowerCase()
        : null;
      const phone = pr_supplier_data.contact_person_phone_number
        ? String(pr_supplier_data.contact_person_phone_number).trim()
        : null;

      const matched = contactOptions.find((c: any) => {
        const cName = String(c?.name || c?.contact_name || "")
          .trim()
          .toLowerCase();
        const cEmail = c?.email ? String(c?.email).trim().toLowerCase() : null;
        const cPhone = c?.phone_number ? String(c?.phone_number).trim() : null;

        return (
          cName === name ||
          (email && cEmail === email) ||
          (phone && cPhone === phone)
        );
      });
      if (matched) {
        formik.setFieldValue("contact_person", matched);
      }
    }
  }, [
    editMode,
    contactOptions,
    pr_supplier_data?.contact_person_name,
    pr_supplier_data?.contact_person_email,
    pr_supplier_data?.contact_person_phone_number,
    formik.values.contact_person,
  ]);

  return (
    <div
      className="p-3 rounded bg-opacity-10 mb-1"
      style={{
        background: MecordColors?.LightThemeBGColor,
        border: `1px solid ${MecordColors?.LightThemeBGColor}`,
      }}
    >
      <Row>
        <Col lg={6} className="mb-3">
          <Form.Label className="fw-semibold">
            Supplier<span className="text-danger">*</span>
          </Form.Label>
          <Autocomplete
            options={supplierOptions}
            loading={supplierLoading}
            disabled={supplierLoading || editMode}
            getOptionLabel={(option: any) => option?.supplier_name || ""}
            value={
              supplierOptions.find(
                (opt: any) => opt.supplier_id === formik.values.supplier_id,
              ) || null
            }
            // onChange={(event, selected) => {
            //   formik.setFieldValue("supplier_id", selected?.supplier_id || "");
            //   formik.setFieldValue("contact_person", null);
            //   formik.setFieldValue("supplier_branch_id", "");
            //   set_pr_supplier_data(null);
            // }}
            onChange={async (event, selected) => {
              const newSupplierId = selected?.supplier_id || "";
              const currentSupplierId = formik.values.supplier_id;
              const hasProducts = formik.values.products?.length > 0;

              // If same supplier selected → do nothing
              if (newSupplierId === currentSupplierId) return;

              // If products exist → show confirmation
              if (hasProducts) {
                const result = await showConfirm(
                  "Changing supplier will remove all added products. Do you want to continue?",
                  "Change Supplier",
                  "Yes, Continue",
                  "Cancel",
                );

                if (!result.isConfirmed) {
                  // ❌ Cancel → stop here (supplier will remain unchanged)
                  return;
                }

                // ✅ Confirm → clear products
                formik.setFieldValue("products", []);
              }

              // Now change supplier
              formik.setFieldValue("supplier_id", newSupplierId);
              formik.setFieldValue("contact_person", null);
              formik.setFieldValue("supplier_branch_id", "");
              set_pr_supplier_data(null);
            }}
            onBlur={() => formik.setFieldTouched("supplier_id", true)}
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ padding: 0 }}>
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.8,
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",

                    // ✅ Hover state
                    "&:hover": {
                      backgroundColor: MecordColors.Hover,
                      color: MecordColors.ActiveText,
                    },

                    // ✅ Selected state
                    ...(selected && {
                      backgroundColor: MecordColors.ActiveBackground,
                      color: MecordColors.ActiveText,
                    }),
                  }}
                >
                  {/* Option Label */}
                  <span>{option?.supplier_name}</span>
                </Box>
              </li>
            )}
            ListboxProps={autocompleteDropdownStyles.ListboxProps}
            renderInput={(params) => (
              <BootstrapTextField
                {...params}
                id="supplier_id"
                name="supplier_id"
                placeholder="Select Supplier"
                error={
                  formik.touched.supplier_id &&
                  Boolean(formik.errors.supplier_id)
                }
                helperText={
                  formik.touched.supplier_id && formik.errors.supplier_id
                }
                fullWidth
                size="small"
                sx={{
                  ...autocompleteDropdownStyles.inputSx,
                }}
              />
            )}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#ced4da",
                },
                "&:hover fieldset": {
                  borderColor: "#ced4da",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#ced4da",
                },
              },
            }}
          />
        </Col>

        <Col lg={6} className="mb-3">
          <Form.Label className="fw-semibold">Supplier Type</Form.Label>
          <Form.Control
            type="text"
            value={pr_supplier_data?.supplier_type || ""}
            placeholder="Auto-filled"
            disabled
          />
        </Col>

        <Col lg={6} className="mb-3">
          <Form.Label className="fw-semibold">Email</Form.Label>
          <Form.Control
            type="text"
            value={pr_supplier_data?.email || ""}
            placeholder="Auto-filled"
            disabled
          />
        </Col>

        <Col lg={6} className="mb-3">
          <Form.Label className="fw-semibold">Phone Number</Form.Label>
          <Form.Control
            type="text"
            value={pr_supplier_data?.phone_number || ""}
            placeholder="Auto-filled"
            disabled
          />
        </Col>
        <Col lg={12} className="mb-3">
          <Form.Label className="fw-semibold">
            Contact Person<span className="text-danger">*</span>
          </Form.Label>
          <Autocomplete
            options={contactOptions}
            getOptionLabel={(option: any) =>
              option?.name || option?.contact_name || ""
            }
            value={
              contactOptions.find(
                (opt: any) =>
                  opt?.supplier_contact_id ===
                  formik.values?.contact_person?.supplier_contact_id,
              ) || null
            }
            disabled={contactOptions.length === 0}
            onChange={(event, selected) => {
              formik.setFieldValue("contact_person", selected || null);
              set_pr_supplier_data((prev: any) => ({
                ...prev,
                contact_person_name: selected?.name,
                contact_person_email: selected?.email,
                contact_person_phone_number: selected?.phone_number,
              }));
            }}
            onBlur={() => formik.setFieldTouched("contact_person", true)}
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ padding: 0 }}>
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.8,
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",

                    // ✅ Hover state
                    "&:hover": {
                      backgroundColor: MecordColors.Hover,
                      color: MecordColors.ActiveText,
                    },

                    // ✅ Selected state
                    ...(selected && {
                      backgroundColor: MecordColors.ActiveBackground,
                      color: MecordColors.ActiveText,
                    }),
                  }}
                >
                  {/* Option Label */}
                  <span>{option?.name || option?.contact_name}</span>
                </Box>
              </li>
            )}
            ListboxProps={autocompleteDropdownStyles.ListboxProps}
            renderInput={(params) => (
              <BootstrapTextField
                {...params}
                id="contact_person"
                name="contact_person"
                placeholder={
                  contactOptions.length === 0
                    ? "No contacts available"
                    : "Select Contact Person"
                }
                error={
                  formik.touched.contact_person &&
                  Boolean(formik.errors.contact_person)
                }
                helperText={
                  formik.touched.contact_person && formik.errors.contact_person
                }
                fullWidth
                size="small"
                sx={{
                  ...autocompleteDropdownStyles.inputSx,
                }}
              />
            )}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#ced4da",
                },
                "&:hover fieldset": {
                  borderColor: "#ced4da",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#ced4da",
                },
              },
            }}
          />
        </Col>
        <Col lg={6} className="mb-3">
          <Form.Label className="fw-semibold">Contact Person Name</Form.Label>
          <Form.Control
            type="text"
            value={pr_supplier_data?.contact_person_name || ""}
            placeholder="Auto-filled"
            disabled
          />
        </Col>
        <Col lg={6} className="mb-3">
          <Form.Label className="fw-semibold">
            Contact Person Phone Number
          </Form.Label>
          <Form.Control
            type="text"
            value={pr_supplier_data?.contact_person_phone_number || ""}
            placeholder="Auto-filled"
            disabled
          />
        </Col>
        <Col lg={12} className="mb-3">
          <Form.Label className="fw-semibold">Contact Person Email</Form.Label>
          <Form.Control
            type="text"
            value={pr_supplier_data?.contact_person_email || ""}
            placeholder="Auto-filled"
            disabled
          />
        </Col>

        <Col lg={12} className="mb-0">
          <Form.Label className="fw-semibold">
            Supplier Branch<span className="text-danger">*</span>
          </Form.Label>
          <Autocomplete
            options={branchOptions}
            getOptionLabel={(option) => option.branch_name}
            value={
              branchOptions.find(
                (opt: any) =>
                  opt.supplier_branch_id === formik.values.supplier_branch_id,
              ) || null
            }
            onChange={(event, selected) => {
              formik.setFieldValue(
                "supplier_branch_id",
                selected?.supplier_branch_id || "",
              );
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ padding: 0 }}>
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.8,
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",

                    // ✅ Hover state
                    "&:hover": {
                      backgroundColor: MecordColors.Hover,
                      color: MecordColors.ActiveText,
                    },

                    // ✅ Selected state
                    ...(selected && {
                      backgroundColor: MecordColors.ActiveBackground,
                      color: MecordColors.ActiveText,
                    }),
                  }}
                >
                  {/* Option Label */}
                  <span>{option.branch_name}</span>
                </Box>
              </li>
            )}
            ListboxProps={autocompleteDropdownStyles.ListboxProps}
            renderInput={(params) => (
              <BootstrapTextField
                {...params}
                name="supplier_branch_id"
                placeholder="Select Branch"
                error={
                  formik.touched.supplier_branch_id &&
                  Boolean(formik.errors.supplier_branch_id)
                }
                helperText={
                  formik.touched.supplier_branch_id &&
                  formik.errors.supplier_branch_id
                }
                fullWidth
                size="small"
                sx={{
                  ...autocompleteDropdownStyles.inputSx,
                }}
              />
            )}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#ced4da",
                },
                "&:hover fieldset": {
                  borderColor: "#ced4da",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#ced4da",
                },
              },
            }}
          />
        </Col>
      </Row>
    </div>
  );
};

export default SupplierDetails;
